/**
 * Settings Screen Component
 *
 * Comprehensive settings including profile, email preferences, display preferences, and privacy.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Settings,
  User,
  Mail,
  Palette,
  Shield,
  Download,
  Trash2,
  BarChart3,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Bell,
  Trophy,
  Sparkles,
  Save,
} from 'lucide-react';
import {
  useUser,
  useUpdateProfile,
  useUpdatePreferences,
  useUpdateEmailPreferences,
  useConsentStatus,
  useUpdateAnalyticsConsent,
  useExportUserData,
  useRequestAccountDeletion,
  useCancelAccountDeletion,
  useDeletionStatus,
  useDeleteAccount,
  CATEGORIES,
  CATEGORY_META,
  type Category,
} from '@have-done/shared';
import { useToast } from './Toast';
import { ConfirmModal } from './ConfirmModal';

/**
 * Toggle switch component for consistent styling
 */
function Toggle({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      className={`
        relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full
        border-2 border-transparent transition-colors duration-200 ease-in-out
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
        disabled:opacity-50 disabled:cursor-not-allowed
        ${checked ? 'bg-primary' : 'bg-bg-tertiary'}
      `}
      role="switch"
      aria-checked={checked}
      aria-label={label}
    >
      <span
        className={`
          pointer-events-none inline-block h-5 w-5 transform rounded-full
          bg-white shadow ring-0 transition duration-200 ease-in-out
          ${checked ? 'translate-x-5' : 'translate-x-0'}
        `}
      />
    </button>
  );
}

/**
 * Section header component
 */
function SectionHeader({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
        <p className="text-sm text-text-secondary">{description}</p>
      </div>
    </div>
  );
}

export function PrivacySettings() {
  const user = useUser();
  const consentStatus = useConsentStatus();
  const deletionStatus = useDeletionStatus();

  const updateProfile = useUpdateProfile();
  const updatePreferences = useUpdatePreferences();
  const updateEmailPreferences = useUpdateEmailPreferences();
  const updateAnalyticsConsent = useUpdateAnalyticsConsent();
  const exportUserData = useExportUserData();
  const requestAccountDeletion = useRequestAccountDeletion();
  const cancelAccountDeletion = useCancelAccountDeletion();
  const deleteAccount = useDeleteAccount();

  const { showSuccess, showError } = useToast();

  // Profile state
  const [name, setName] = useState('');
  const [isNameEditing, setIsNameEditing] = useState(false);
  const [isSavingName, setIsSavingName] = useState(false);

  // Email preferences state
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);

  // Display preferences state
  const [isUpdatingDisplay, setIsUpdatingDisplay] = useState(false);

  // Privacy state
  const [isExporting, setIsExporting] = useState(false);
  const [isUpdatingConsent, setIsUpdatingConsent] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImmediateDeleteModal, setShowImmediateDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Initialize name when user loads
  const displayName = user?.name ?? '';

  // Email preferences (with defaults)
  const emailPrefs = user?.emailPreferences ?? {
    weeklySummary: true,
    streakMilestones: true,
    bigWinCelebrations: false,
    marketingEmails: false,
  };

  // Display preferences (with defaults)
  const displayPrefs = user?.preferences ?? {
    theme: 'system' as const,
    defaultCategory: 'personal' as const,
  };

  // Handle name save
  const handleSaveName = async () => {
    if (!name.trim()) {
      showError('Name cannot be empty');
      return;
    }
    setIsSavingName(true);
    try {
      await updateProfile({ name: name.trim() });
      showSuccess('Name updated');
      setIsNameEditing(false);
    } catch {
      showError('Failed to update name');
    } finally {
      setIsSavingName(false);
    }
  };

  // Handle email preference toggle
  const handleEmailPrefToggle = async (
    key: 'weeklySummary' | 'streakMilestones' | 'bigWinCelebrations' | 'marketingEmails'
  ) => {
    setIsUpdatingEmail(true);
    try {
      await updateEmailPreferences({ [key]: !emailPrefs[key] });
      showSuccess('Email preferences updated');
    } catch {
      showError('Failed to update preferences');
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  // Handle default category change
  const handleDefaultCategoryChange = async (category: Category) => {
    setIsUpdatingDisplay(true);
    try {
      await updatePreferences({ defaultCategory: category });
      showSuccess('Default category updated');
    } catch {
      showError('Failed to update preferences');
    } finally {
      setIsUpdatingDisplay(false);
    }
  };

  // Handle analytics toggle
  const handleAnalyticsToggle = async () => {
    if (!consentStatus) return;
    setIsUpdatingConsent(true);
    try {
      const newStatus = consentStatus.analytics === 'accepted' ? 'rejected' : 'accepted';
      await updateAnalyticsConsent({ analytics: newStatus });
      showSuccess(newStatus === 'accepted' ? 'Analytics enabled' : 'Analytics disabled');
    } catch {
      showError('Failed to update preferences');
    } finally {
      setIsUpdatingConsent(false);
    }
  };

  // Handle data export
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const data = await exportUserData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `have-done-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showSuccess('Data exported successfully');
    } catch {
      showError('Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle deletion request
  const handleRequestDeletion = async () => {
    setIsDeleting(true);
    try {
      await requestAccountDeletion({ reason: 'User requested deletion' });
      showSuccess('Deletion scheduled. You have 30 days to cancel.');
      setShowDeleteModal(false);
    } catch {
      showError('Failed to request deletion');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle cancel deletion
  const handleCancelDeletion = async () => {
    try {
      await cancelAccountDeletion();
      showSuccess('Deletion cancelled');
    } catch {
      showError('Failed to cancel deletion');
    }
  };

  // Handle immediate deletion
  const handleImmediateDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteAccount({ confirmDeletion: true, reason: 'Immediate deletion requested' });
      // User will be logged out automatically
    } catch {
      showError('Failed to delete account');
      setIsDeleting(false);
    }
  };

  // Loading state
  if (!user || !consentStatus || !deletionStatus) {
    return (
      <div className="animate-pulse space-y-8">
        <div className="h-8 bg-bg-secondary rounded w-1/3"></div>
        <div className="h-32 bg-bg-secondary rounded"></div>
        <div className="h-32 bg-bg-secondary rounded"></div>
        <div className="h-32 bg-bg-secondary rounded"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center">
          <Settings className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-display font-bold text-text-primary">Settings</h1>
          <p className="text-text-secondary">Manage your account and preferences</p>
        </div>
      </div>

      {/* Pending deletion warning */}
      {deletionStatus.hasPendingDeletion && (
        <div className="bg-error/5 border border-error/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-error shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-error">Account Deletion Scheduled</h3>
              <p className="text-sm text-text-secondary mt-1">
                Your account will be permanently deleted in{' '}
                <strong>{deletionStatus.daysRemaining} days</strong>. All your data will be erased.
              </p>
              <button
                type="button"
                onClick={handleCancelDeletion}
                className="mt-3 text-sm font-medium text-primary hover:text-primary-dark transition-colors"
              >
                Cancel Deletion
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Section */}
      <section>
        <SectionHeader icon={User} title="Profile" description="Your account information" />
        <div className="bg-bg-secondary rounded-lg p-4 space-y-4">
          {/* Email (read-only) */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Email</label>
            <p className="text-text-primary">{user.email}</p>
          </div>

          {/* Name (editable) */}
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-text-secondary mb-1"
            >
              Display Name
            </label>
            {isNameEditing ? (
              <div className="flex gap-2">
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg border border-border-light bg-bg-primary text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter your name"
                />
                <button
                  type="button"
                  onClick={handleSaveName}
                  disabled={isSavingName}
                  className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:bg-primary-dark transition-colors disabled:opacity-50"
                >
                  <Save className="w-4 h-4 inline mr-1" />
                  {isSavingName ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsNameEditing(false);
                    setName('');
                  }}
                  className="px-4 py-2 rounded-lg text-text-secondary hover:bg-bg-tertiary transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-text-primary">{displayName || 'Not set'}</p>
                <button
                  type="button"
                  onClick={() => {
                    setName(displayName);
                    setIsNameEditing(true);
                  }}
                  className="text-sm text-primary hover:text-primary-dark transition-colors"
                >
                  Edit
                </button>
              </div>
            )}
          </div>

          {/* Auth provider (read-only) */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Sign-in Method
            </label>
            <p className="text-text-primary capitalize">{user.authProvider}</p>
          </div>
        </div>
      </section>

      {/* Email Preferences Section */}
      <section>
        <SectionHeader icon={Mail} title="Email Notifications" description="Choose what emails you receive" />
        <div className="bg-bg-secondary rounded-lg divide-y divide-border-light">
          {/* Weekly Summary */}
          <div className="p-4 flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <Bell className="w-5 h-5 text-text-tertiary shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-text-primary">Weekly Summary</h3>
                <p className="text-sm text-text-secondary">
                  Get a recap of your accomplishments every Sunday
                </p>
              </div>
            </div>
            <Toggle
              checked={emailPrefs.weeklySummary}
              onChange={() => handleEmailPrefToggle('weeklySummary')}
              disabled={isUpdatingEmail}
              label="Weekly summary emails"
            />
          </div>

          {/* Streak Milestones */}
          <div className="p-4 flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <Trophy className="w-5 h-5 text-text-tertiary shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-text-primary">Streak Milestones</h3>
                <p className="text-sm text-text-secondary">
                  Celebrate when you hit streak milestones (7, 30, 100 days)
                </p>
              </div>
            </div>
            <Toggle
              checked={emailPrefs.streakMilestones}
              onChange={() => handleEmailPrefToggle('streakMilestones')}
              disabled={isUpdatingEmail}
              label="Streak milestone emails"
            />
          </div>

          {/* Big Win Celebrations */}
          <div className="p-4 flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-text-tertiary shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-text-primary">Big Win Celebrations</h3>
                <p className="text-sm text-text-secondary">
                  Get a congratulatory email when you log a big win
                </p>
              </div>
            </div>
            <Toggle
              checked={emailPrefs.bigWinCelebrations}
              onChange={() => handleEmailPrefToggle('bigWinCelebrations')}
              disabled={isUpdatingEmail}
              label="Big win celebration emails"
            />
          </div>
        </div>
      </section>

      {/* Display Preferences Section */}
      <section>
        <SectionHeader icon={Palette} title="Display Preferences" description="Customize your experience" />
        <div className="bg-bg-secondary rounded-lg p-4">
          {/* Default Category */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Default Category
            </label>
            <p className="text-sm text-text-tertiary mb-3">
              Pre-selected category when adding new accomplishments
            </p>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((cat) => {
                const meta = CATEGORY_META[cat];
                const isSelected = displayPrefs.defaultCategory === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => handleDefaultCategoryChange(cat)}
                    disabled={isUpdatingDisplay}
                    className={`
                      px-3 py-1.5 rounded-full text-sm font-medium transition-colors
                      disabled:opacity-50 disabled:cursor-not-allowed
                      ${
                        isSelected
                          ? 'bg-primary text-white'
                          : 'bg-bg-tertiary text-text-secondary hover:bg-bg-primary hover:text-text-primary'
                      }
                    `}
                  >
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Privacy & Data Section */}
      <section>
        <SectionHeader icon={Shield} title="Privacy & Data" description="Manage your data and privacy" />
        <div className="space-y-4">
          {/* Analytics consent */}
          <div className="bg-bg-secondary rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <BarChart3 className="w-5 h-5 text-text-tertiary shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-text-primary">Analytics</h3>
                  <p className="text-sm text-text-secondary mt-1">
                    Help us improve Have-Done by sharing anonymous usage data. We use PostHog (EU
                    servers) and never sell your data.
                  </p>
                  <Link
                    to="/privacy"
                    className="text-sm text-primary hover:underline mt-1 inline-block"
                  >
                    Learn more
                  </Link>
                </div>
              </div>
              <Toggle
                checked={consentStatus.analytics === 'accepted'}
                onChange={handleAnalyticsToggle}
                disabled={isUpdatingConsent}
                label="Enable analytics"
              />
            </div>
            {consentStatus.analytics === 'accepted' && (
              <div className="mt-3 flex items-center gap-2 text-xs text-success">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>Analytics enabled</span>
              </div>
            )}
          </div>

          {/* Data export */}
          <div className="bg-bg-secondary rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Download className="w-5 h-5 text-text-tertiary shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-medium text-text-primary">Export Your Data</h3>
                <p className="text-sm text-text-secondary mt-1">
                  Download all your data in JSON format. Includes entries, streaks, preferences, and
                  activity history.
                </p>
                <button
                  type="button"
                  onClick={handleExport}
                  disabled={isExporting}
                  className="
                    mt-3 px-4 py-2 rounded-lg
                    text-sm font-medium text-primary
                    bg-primary/10 hover:bg-primary/20
                    transition-colors
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
                    disabled:opacity-50 disabled:cursor-not-allowed
                  "
                >
                  {isExporting ? 'Exporting...' : 'Download My Data'}
                </button>
              </div>
            </div>
          </div>

          {/* Delete account */}
          <div className="bg-bg-secondary rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Trash2 className="w-5 h-5 text-error shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-medium text-text-primary">Delete Account</h3>
                <p className="text-sm text-text-secondary mt-1">
                  Permanently delete your account and all associated data. This action cannot be
                  undone.
                </p>
                <div className="mt-3 flex flex-wrap gap-3">
                  {!deletionStatus.hasPendingDeletion && (
                    <>
                      <button
                        type="button"
                        onClick={() => setShowDeleteModal(true)}
                        className="
                          px-4 py-2 rounded-lg
                          text-sm font-medium text-error
                          bg-error/10 hover:bg-error/20
                          transition-colors
                          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error
                        "
                      >
                        <Clock className="w-4 h-4 inline mr-1.5" />
                        Schedule Deletion (30 days)
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowImmediateDeleteModal(true)}
                        className="
                          px-4 py-2 rounded-lg
                          text-sm font-medium text-text-secondary
                          hover:text-error hover:bg-error/10
                          transition-colors
                          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error
                        "
                      >
                        Delete Immediately
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Delete modal (30-day grace period) */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleRequestDeletion}
        title="Schedule Account Deletion?"
        message="Your account will be scheduled for deletion in 30 days. During this time, you can cancel the request and keep your data. After 30 days, all data will be permanently erased."
        confirmText="Schedule Deletion"
        cancelText="Keep Account"
        variant="warning"
        isLoading={isDeleting}
      />

      {/* Immediate delete modal */}
      <ConfirmModal
        isOpen={showImmediateDeleteModal}
        onClose={() => setShowImmediateDeleteModal(false)}
        onConfirm={handleImmediateDelete}
        title="Delete Account Immediately?"
        message="This will permanently delete your account and ALL your data right now. This action CANNOT be undone. Are you absolutely sure?"
        confirmText="Delete Forever"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}
